import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

/**
 * Scoped, customer-facing Supabase clients.
 *
 * Row level security on the booking funnel tables requires the caller to prove
 * it holds the private code contained in the customer's own link:
 *   - funnel pages send the booking's random UUID as `x-booking-id`
 *   - the signing page sends the signature access token as
 *     `x-customer-access-token`
 *
 * These clients never persist a session, so they cannot collide with the shared
 * admin client in `./client`.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type ScopedClient = SupabaseClient<Database>;

const cache = new Map<string, ScopedClient>();

function createScopedClient(headerName: string, headerValue: string): ScopedClient {
  const cacheKey = `${headerName}:${headerValue}`;
  const existing = cache.get(cacheKey);
  if (existing) return existing;

  const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: `sb-scoped-${headerName}`,
    },
    global: {
      headers: { [headerName]: headerValue },
    },
  });

  cache.set(cacheKey, client);
  return client;
}

/** Client scoped to a single booking, for the customer booking funnel. */
export function createBookingClient(bookingId: string): ScopedClient {
  return createScopedClient('x-booking-id', bookingId);
}

/** Client scoped to a single signature request, for the signing page. */
export function createSignatureClient(accessToken: string): ScopedClient {
  return createScopedClient('x-customer-access-token', accessToken);
}

const CHAT_SESSION_STORAGE_KEY = 'vibe_chat_session_key';

/**
 * Stable per-visitor chat session key. Anonymous chat conversations are locked
 * to this key, so one visitor can never read another visitor's chat.
 */
export function getChatSessionKey(): string {
  try {
    const existing = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const key = crypto.randomUUID();
    localStorage.setItem(CHAT_SESSION_STORAGE_KEY, key);
    return key;
  } catch {
    // Storage blocked (private mode): fall back to an in-memory key.
    return (globalThis as any).__vibeChatSessionKey ??=  crypto.randomUUID();
  }
}

/** Client scoped to the current visitor's public chat conversation. */
export function createChatClient(): ScopedClient {
  return createScopedClient('x-chat-session', getChatSessionKey());
}
